import type { Issue, BoardFilterState, User } from '@/types'

// 主要篩選函數
export const applyFilters = (
  issues: Issue[], 
  filters: BoardFilterState, 
  currentUser: User
): Issue[] => {
  return issues.filter(issue => {
    // 1. 標籤篩選
    if (!applyLabelFilter(issue, filters.labels, filters.labelLogic)) {
      return false
    }
    
    // 2. 指派篩選
    if (!applyAssigneeFilter(issue, filters, currentUser)) {
      return false
    }
    
    // 3. 優先級篩選
    if (!applyPriorityFilter(issue, filters.priorities)) {
      return false
    }
    
    // 4. 到期日篩選
    if (!applyDueDateFilter(issue, filters.dueDateFilter)) {
      return false
    }
    
    // 5. 搜尋篩選
    if (!applySearchFilter(issue, filters.searchQuery)) {
      return false
    }
    
    return true
  })
}

// 標籤篩選邏輯
export const applyLabelFilter = (
  issue: Issue, 
  selectedLabels: string[], 
  logic: 'AND' | 'OR'
): boolean => {
  if (selectedLabels.length === 0) return true
  
  const issueLabels = issue.labels?.map(label => label.id) || []
  
  if (logic === 'AND') {
    return selectedLabels.every(labelId => issueLabels.includes(labelId))
  } else {
    return selectedLabels.some(labelId => issueLabels.includes(labelId))
  }
}

// 指派篩選邏輯
export const applyAssigneeFilter = (
  issue: Issue, 
  filters: BoardFilterState, 
  currentUser: User
): boolean => {
  // 快速篩選
  if (filters.showMyIssues && issue.assigneeId !== currentUser.id) {
    return false
  }
  
  if (filters.showCreatedByMe && issue.reporterId !== currentUser.id) {
    return false
  }
  
  if (filters.showUnassigned && issue.assigneeId) {
    return false
  }
  
  // 特定成員篩選
  if (filters.assignees.length > 0) {
    const assigneeId = issue.assigneeId || 'unassigned'
    return filters.assignees.includes(assigneeId)
  }
  
  return true
}

// 優先級篩選邏輯
export const applyPriorityFilter = (
  issue: Issue, 
  selectedPriorities: string[]
): boolean => {
  if (selectedPriorities.length === 0) return true
  return selectedPriorities.includes(issue.priority)
}

// 到期日篩選邏輯
export const applyDueDateFilter = (
  issue: Issue, 
  dueDateFilter: BoardFilterState['dueDateFilter']
): boolean => {
  if (!dueDateFilter.type) return true
  if (!issue.dueDate) return false
  
  const dueDate = new Date(issue.dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  switch (dueDateFilter.type) {
    case 'overdue':
      return dueDate < today
      
    case 'today': {
      const todayEnd = new Date(today)
      todayEnd.setHours(23, 59, 59, 999)
      return dueDate >= today && dueDate <= todayEnd
    }
      
    case 'week': {
      const weekEnd = new Date(today)
      weekEnd.setDate(today.getDate() + 7)
      return dueDate >= today && dueDate <= weekEnd
    }
      
    case 'custom': {
      if (!dueDateFilter.customRange) return true
      const { from, to } = dueDateFilter.customRange
      if (!from || !to) return true
      return dueDate >= from && dueDate <= to
    }
      
    default:
      return true
  }
}

// 搜尋篩選邏輯
export const applySearchFilter = (
  issue: Issue, 
  searchQuery: string
): boolean => {
  if (!searchQuery.trim()) return true
  
  const query = searchQuery.toLowerCase()
  return (
    issue.title.toLowerCase().includes(query) ||
    (issue.description && issue.description.toLowerCase().includes(query)) ||
    issue.key.toLowerCase().includes(query)
  )
}

// 計算篩選統計
export const calculateFilterStats = (issues: Issue[]) => {
  const stats = {
    labelCounts: new Map<string, number>(),
    priorityCounts: new Map<string, number>(),
    assigneeCounts: new Map<string, number>(),
    dueDateCounts: {
      overdue: 0,
      today: 0,
      week: 0,
      withDueDate: 0
    }
  }
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const weekEnd = new Date(today)
  weekEnd.setDate(today.getDate() + 7)
  
  issues.forEach(issue => {
    // 標籤統計
    issue.labels?.forEach(label => {
      stats.labelCounts.set(label.id, (stats.labelCounts.get(label.id) || 0) + 1)
    })
    
    // 優先級統計
    stats.priorityCounts.set(
      issue.priority, 
      (stats.priorityCounts.get(issue.priority) || 0) + 1
    )
    
    // 指派統計
    const assigneeId = issue.assigneeId || 'unassigned'
    stats.assigneeCounts.set(
      assigneeId, 
      (stats.assigneeCounts.get(assigneeId) || 0) + 1
    )
    
    // 到期日統計
    if (issue.dueDate) {
      const dueDate = new Date(issue.dueDate)
      stats.dueDateCounts.withDueDate++
      
      if (dueDate < today) {
        stats.dueDateCounts.overdue++
      } else if (dueDate >= today && dueDate <= new Date(today.getTime() + 24 * 60 * 60 * 1000)) {
        stats.dueDateCounts.today++
      } else if (dueDate >= today && dueDate <= weekEnd) {
        stats.dueDateCounts.week++
      }
    }
  })
  
  return stats
}

// 初始篩選狀態
export const initialFilterState: BoardFilterState = {
  labels: [],
  labelLogic: 'OR',
  assignees: [],
  showMyIssues: false,
  showUnassigned: false,
  showCreatedByMe: false,
  priorities: [],
  dueDateFilter: { type: null },
  searchQuery: '',
  isExpanded: false,
  activeSection: null
}

// 檢查是否有任何篩選被啟用
export const hasActiveFilters = (filters: BoardFilterState): boolean => {
  return (
    filters.labels.length > 0 ||
    filters.assignees.length > 0 ||
    filters.showMyIssues ||
    filters.showUnassigned ||
    filters.showCreatedByMe ||
    filters.priorities.length > 0 ||
    filters.dueDateFilter.type !== null ||
    filters.searchQuery.trim() !== ''
  )
}

// 清除所有篩選
export const clearAllFilters = (): BoardFilterState => ({
  ...initialFilterState,
  isExpanded: false
})